import { Component, Input, Output, EventEmitter, OnDestroy, OnInit } from '@angular/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Language } from '../../models/language';
import { HelpModalComponent } from '../../../features/ticket-purchase/components/help-modal/help-modal.component';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  standalone: true,
  imports: [TranslateModule, HelpModalComponent]
})
export class HeaderComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly LANGUAGE_PREFERENCE_KEY = 'rejsebillet_lang';

  @Input() showHelpButton: boolean = false;
  @Input() showVisDetaljer: boolean = false;
  @Input() showTicketContent: boolean = false;
  @Output() openDetails = new EventEmitter<void>();
  @Output() closeDetails = new EventEmitter<void>();

  currentLanguage: Language = Language.Danish;
  isHelpModalOpen: boolean = false;

  constructor(private translateService: TranslateService) { }

  ngOnInit(): void {
    this.initializeLanguage();
  }

  private initializeLanguage(): void {
    try {
      const savedLanguage = localStorage.getItem(this.LANGUAGE_PREFERENCE_KEY) as Language;
      if (savedLanguage && Object.values(Language).includes(savedLanguage)) {
        this.currentLanguage = savedLanguage;
        this.translateService.use(this.currentLanguage);
      } else {
        this.currentLanguage = this.translateService.getCurrentLang() as Language;
      }
    } catch (error) {
      console.error('Error initializing language:', error);
      this.currentLanguage = Language.English;
    }
  }

  openTicketDetails(): void {
    this.openDetails.emit();
  }

  openTicketContent(): void {
    this.closeDetails.emit();
  }

  openHelp(): void {
    const helpUrl = this.translateService.instant('web.helpLink');
    if (helpUrl) {
      window.open(helpUrl, '_blank');
    }
  }

  //Keep Help Modal for future use if needed
  openHelpModal(): void {
    this.isHelpModalOpen = true;
  }

  closeHelpModal(): void {
    this.isHelpModalOpen = false;
  }

  get switchLanguageText(): string {
    try {
      return this.currentLanguage === Language.Danish
        ? this.translateService.instant('web.english')
        : this.translateService.instant('web.danish');
    } catch (error) {
      console.error('Error getting language text:', error);
      return this.currentLanguage === Language.Danish ? 'English' : 'Dansk';
    }
  }

  toggleLanguage(): void {
    try {
      this.currentLanguage = this.currentLanguage === Language.Danish
        ? Language.English
        : Language.Danish;

      this.saveLanguagePreference(this.currentLanguage);
      this.translateService.use(this.currentLanguage);
    } catch (error) {
      console.error('Error toggling language:', error);
    }
  }

  private saveLanguagePreference(language: Language): void {
    try {
      localStorage.setItem(this.LANGUAGE_PREFERENCE_KEY, language);
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}